import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Subscribers() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/subscribers").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the launch list? They will stop receiving launch emails.`)) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      toast.success("Removed from the launch list");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const exportCsv = () => {
    const rows = [["Email", "Signed up"], ...data.subscribers.map((s) => [s.email, s.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "open-horizon-launch-list.csv";
    a.click();
    toast.success("Launch list exported");
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-2xl text-slate-50">Launch List</h1>
        {data && data.count > 0 && (
          <button onClick={exportCsv} className="btn-ghost-gold !px-5 !py-2.5 text-xs" data-testid="subscribers-export-btn">
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>
      <p className="text-sm text-slate-400 font-light mb-8 max-w-lg">
        Fans who left their email on the countdown banner. They're emailed automatically the moment a countdown
        reaches zero — and get a welcome email when they join.
      </p>
      {data && data.count > 0 ? (
        <>
          <p className="text-xs tracking-widest uppercase text-gold-400/70 mb-4" data-testid="subscribers-total">
            {data.count} {data.count === 1 ? "fan" : "fans"} on the list
          </p>
          <div className="space-y-2 max-w-xl">
            {data.subscribers.map((s) => (
              <div key={s.id} className="flex items-center gap-4 rounded-xl border border-white/5 bg-navy-900/50 px-4 py-3"
                data-testid={`subscriber-row-${s.id}`}>
                <span className="text-sm text-slate-100 flex-1 min-w-0 truncate">{s.email}</span>
                <span className="text-[11px] text-slate-500 shrink-0">{fmtWhen(s.created_at)}</span>
                <button onClick={() => remove(s.id, s.email)} className="text-slate-500 hover:text-red-400 transition-colors p-1"
                  data-testid={`subscriber-delete-${s.id}`} aria-label={`Remove ${s.email}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500" data-testid="subscribers-empty">
          No fans yet — the "Notify me" box on the countdown banner collects them.
        </p>
      )}
    </div>
  );
}
